/**
 * Central configuration module for the RealityCheq scorecard.
 * Single source of truth shared by the corpus annotation pipeline, the claims
 * scoring/aggregation pipeline, and the Kodakku annotation UI.
 *
 * No Deno-specific or npm imports — plain TypeScript importable by both runtimes.
 *
 * Consumers:
 *   - supabase/functions/corpus/config.ts (auto-generates LLM extraction questions)
 *   - _shared/services/canonical/claim_verification/internal/ (scoring, verdict aggregation)
 *   - platform/src/pages/internal/Annotate/ (Kodakku annotation UI)
 */

// ── Dimension types ───────────────────────────────────────────────────────────

/**
 * Annotation tier for a scorecard dimension.
 * 1 = gates ingestion (must be filled before a source can be ingested).
 * 2 = improves quality score but does not gate ingestion.
 * 3 = opportunistic (nice to have, not required).
 */
export type ExtractionTier = 1 | 2 | 3;

export type ScorecardKey =
  | "study_type"
  | "sampling_method"
  | "sample_size"
  | "retention_rate"
  | "population_diversity"
  | "confounding"
  | "causation"
  | "statistical_rigor"
  | "statistical_power"
  | "bias"
  | "funding"
  | "peer_review";

export interface DimensionConfig<T extends string = string> {
  /** Valid labels, in display and LLM prompt order. */
  labels: T[];
  /**
   * Annotation tier for this dimension.
   * Tier 1 gates ingestion; Tier 2 improves quality; Tier 3 is opportunistic.
   */
  tier: ExtractionTier;
  /** Quality scoring points per label. Must cover every label in this dimension. */
  scoring: Record<string, number>;
  /**
   * If true, at most one label may be selected (single-select dimension).
   * The annotation UI and LLM sanitizer enforce this constraint.
   */
  singleSelect?: true;
  /**
   * If true, the evidence Tangrowth returns for this dimension is the model's argument for the
   * labels, not a verbatim quote from the paper. It is stored as the field's `justification` and
   * never as a `quote`, which the review UI shows in quotation marks and looks for in the text.
   */
  evidenceIsJustification?: true;
  /**
   * Custom mutually exclusive label subgroups for multi-select dimensions.
   * Only needed when singleSelect is absent and partial mutual exclusion applies.
   * Each inner array is a group from which at most one label can be selected.
   */
  mutuallyExclusiveGroups?: T[][];
  /**
   * One-sentence description of what this dimension measures, shown in the Kodakku help panel.
   * Answers: "What does this dimension capture and when does it apply?"
   */
  description?: string;
  /**
   * Per-label descriptions for LLM prompt generation and Kodakku help/tooltip display.
   * When present, the Python generator emits a `description` property on the StrEnum and
   * the annotation UI shows them as tooltips (override mode) and in the help panel.
   * Keys must cover every label in this dimension.
   */
  labelDescriptions?: Record<T, string>;
  /**
   * Named subsets of labels emitted as typed classmethods on the Python StrEnum.
   * Each key becomes a classmethod name (e.g. "non_random" → `cls.non_random()`).
   */
  labelSubsets?: Record<string, T[]>;
  /**
   * Numeric band thresholds for emitting a `from_number(cls, n: float)` classmethod.
   * Each key is the inclusive lower bound for its label. The generator sorts thresholds
   * descending and emits `if n >= threshold` for all but the lowest; the lowest becomes
   * the bare fallthrough `return`. Labels not listed here are excluded from `from_number`
   * (e.g. "Retention not stated") — no implicit below-minimum behaviour.
   */
  bands?: Record<number, T>;
}

// ── Scorecard dimensions ──────────────────────────────────────────────────────

/** The study_type leaves, in display order. Declared once so the weight table and scoring
 *  profiles (design_scoring_profiles.ts) can be typed against them. */
export const STUDY_TYPE_LABELS = [
  "RCT",
  "Non-Randomised Controlled Trial",
  "Uncontrolled Trial",
  "Comparative Prospective Cohort",
  "Comparative Retrospective Cohort",
  "Uncontrolled Prospective Cohort",
  "Uncontrolled Retrospective Cohort",
  "Case-Control",
  "Cross-Sectional",
  "Other Observational",
  "Anecdotal",
  "Animal",
  "Cell",
  "Animal and Cell",
] as const;

export type StudyTypeLabel = (typeof STUDY_TYPE_LABELS)[number];

/** Whether a stored string is a current study_type leaf (not a retired or unknown label). */
export function isStudyTypeLabel(label: string): label is StudyTypeLabel {
  return (STUDY_TYPE_LABELS as readonly string[]).includes(label);
}

/**
 * Computes the most points a dimension can award: the best label of each group of mutually
 * exclusive labels (a single-select dimension is one such group) plus every label that combines
 * freely with the others.
 */
export function computeDimensionMaxPoints(
  dimension: Pick<DimensionConfig, "scoring" | "singleSelect" | "mutuallyExclusiveGroups">
): number {
  const { scoring } = dimension;
  const exclusiveGroups = dimension.singleSelect
    ? [Object.keys(scoring)]
    : (dimension.mutuallyExclusiveGroups ?? []);
  const grouped = new Set(exclusiveGroups.flat());
  const bestOfGroups = exclusiveGroups.reduce(
    (sum, group) => sum + Math.max(0, ...group.map((label) => scoring[label] ?? 0)),
    0
  );
  return Object.entries(scoring)
    .filter(([label]) => !grouped.has(label))
    .reduce((sum, [, points]) => sum + Math.max(0, points), bestOfGroups);
}

/** The study_type entry: its label-typed config plus the study types read as research papers. */
export interface StudyTypeConfig extends DimensionConfig<StudyTypeLabel> {
  paperStudyTypes: StudyTypeLabel[];
}

export const SCORECARD: Record<ScorecardKey, DimensionConfig> & {
  study_type: StudyTypeConfig;
} = {
  study_type: {
    tier: 1,
    description:
      "The study type: whether the study assigned the exposure or observed it, and whether there was a comparison group. Exactly one study type per document — the one behind the paper's primary finding (the lower-weight study type when findings are co-primary).",
    labels: [...STUDY_TYPE_LABELS],
    evidenceIsJustification: true,
    // TODO: remove LLM prompt hints from those descriptions.
    labelDescriptions: {
      RCT: "The study assigned the exposure, and did so randomly. The study assigns an exposure when its protocol supplies or allocates it, or fixes its regimen — a drug, a diet, an exercise programme, a procedure — whatever participants' adherence. Random allocation to arms counts, and so does a random order of phases in a crossover; random sampling of participants does not.",
      "Non-Randomised Controlled Trial":
        "The study assigned the exposure, but not randomly (e.g. by alternation, site, or participant or clinician preference), and the finding compares the exposed with a reference measured the same way: a separate group of participants, studied at the same time or an earlier group from the same setting with individual data, or the same participants under a control condition the protocol gives (placebo, sham, usual care) in a phase of its own, as in a fixed-order crossover. The reference has no exposure, a lower one, or a different one when the finding is the head-to-head comparison, and it is measured the same way as the exposed: same definitions, same way of collecting the data, same period. Figures gathered differently (published background rates, another registry's counts) are not one. A measurement of the same participants before the exposure starts is a baseline, not a reference.",
      "Uncontrolled Trial":
        "The study assigned the exposure, and the finding rests on participants' change from their own baseline, or on aggregate figures from the literature or registries — not on a reference measured the same way. Several groups each compared only with their own baseline are still uncontrolled.",
      "Comparative Prospective Cohort":
        "The exposure was not assigned by the study. It was on record before the outcome occurred, participants were followed forward in time, and the finding compares the exposed with a reference group measured the same way — participants studied at the same time, or an earlier group from the same setting with individual data — that has no exposure, a lower one (the lowest-intake group of a dose-response analysis counts), or a different one when the finding is the head-to-head comparison. A cohort is defined by time, not by how participants were recruited, so population-based cohorts qualify.",
      "Comparative Retrospective Cohort":
        "As a comparative prospective cohort — exposure on record before the outcome, compared with a reference group — but both events had already occurred and the timeline is reconstructed from existing records.",
      "Uncontrolled Prospective Cohort":
        "A defined group followed forward in time from an exposure on record, where the finding rests on each group's change from its own baseline, or on aggregate figures from the literature or registries — not on a reference group, however many groups were followed.",
      "Uncontrolled Retrospective Cohort":
        "A defined group whose exposure and outcome are reconstructed from existing records, where the finding rests on each group's change from its own baseline, or on aggregate figures from the literature or registries — not on a reference group, however many groups were followed.",
      "Case-Control":
        "Participants were selected on the outcome — cases who already have it versus controls who do not — and exposure is measured backward. A nested case-control is still Case-Control.",
      "Cross-Sectional":
        "A one-timepoint snapshot of a defined population: exposure and outcome measured together, with no follow-up and no selection on the outcome. Cases and controls selected on the outcome are Case-Control even when measured once.",
      "Other Observational":
        "A systematic study of humans, whose exposure the study did not assign, that fits none of the named observational study types. Reached only once every named study type is ruled out; never a default for a paper that merely calls itself observational.",
      Anecdotal:
        "Not systematic: an ad-hoc description of encountered cases, with no defined population and no denominator. A study is systematic when it defines who it studies, gives a denominator, and ascertains exposure and outcome by a consistent protocol — could a rate be computed from it?",
      Animal:
        "Experiments on live non-human animals only. Applies regardless of the experiment's internal methodology: a randomised control group of rats is Animal, not RCT.",
      Cell: "Experiments on cell lines or tissue cultures only, with no live animal subjects.",
      "Animal and Cell":
        "A paper reporting both live-animal and in-vitro experiments, with no human-participant study.",
    },
    // study_type is never point-scored: its evidentiary weight comes from STUDY_TYPE_WEIGHTS
    // (design_scoring_profiles.ts). Extending this to the 14 leaves would invent a second,
    // unapproved scale that double-counts the weight.
    scoring: {},
    singleSelect: true,
    // Sources read as research papers (source_type "paper"); every other study type is an "article".
    paperStudyTypes: [
      "RCT",
      "Non-Randomised Controlled Trial",
      "Uncontrolled Trial",
      "Comparative Prospective Cohort",
      "Comparative Retrospective Cohort",
      "Uncontrolled Prospective Cohort",
      "Uncontrolled Retrospective Cohort",
      "Animal",
      "Cell",
      "Animal and Cell",
    ],
  },
  sampling_method: {
    tier: 2,
    description:
      "How participants were selected into the study — governs selection from a population, not treatment assignment after enrolment.",
    labels: [
      "Random sampling",
      "Non-random sampling with justification",
      "Non-random sampling without justification",
    ],
    scoring: {
      "Random sampling": 10,
      "Non-random sampling with justification": 5,
      "Non-random sampling without justification": 0,
    },
    singleSelect: true,
    labelDescriptions: {
      "Random sampling":
        "A formal probability mechanism SELECTED participants from a target population. This must govern participant SELECTION, not treatment assignment — language describing random allocation to arms or groups after enrolment does NOT qualify, however the trial itself is otherwise described.",
      "Non-random sampling with justification":
        "Participants were not probability-sampled, but the selection criterion itself is the scientific reason for studying this population — e.g. a disease, condition, or exposure that defines the group. The criterion IS the justification; no separate explanatory sentence is required.",
      "Non-random sampling without justification":
        "Participants were not probability-sampled and there is no scientifically meaningful selection criterion — convenience recruitment, or a general-purpose population used with no rationale specific to this study. Eligibility thresholds that only gate who CAN enrol are not a justification.",
    },
    labelSubsets: {
      non_random: [
        "Non-random sampling with justification",
        "Non-random sampling without justification",
      ],
    },
  },
  sample_size: {
    tier: 2,
    description: "Total number of participants enrolled in the study.",
    labels: [
      "500,000+",
      "50,001–500,000",
      "5,001–50,000",
      "501–5,000",
      "51–500",
      "31–50",
      "11–30",
      "1–10",
    ],
    scoring: {
      "500,000+": 10,
      "50,001–500,000": 9,
      "5,001–50,000": 8,
      "501–5,000": 6,
      "51–500": 4,
      "31–50": 2,
      "11–30": 1,
      "1–10": 0,
    },
    singleSelect: true,
    bands: {
      500001: "500,000+",
      50001: "50,001–500,000",
      5001: "5,001–50,000",
      501: "501–5,000",
      51: "51–500",
      31: "31–50",
      11: "11–30",
      1: "1–10",
    },
  },
  retention_rate: {
    tier: 3,
    description:
      "Percentage of enrolled participants who completed the study and contributed to the final analysis.",
    labels: [
      "Retention ≥95%",
      "Retention 85–94%",
      "Retention 70–84%",
      "Retention 50–69%",
      "Retention <50%",
      "Retention not stated",
    ],
    scoring: {
      "Retention ≥95%": 5,
      "Retention 85–94%": 4,
      "Retention 70–84%": 2,
      "Retention 50–69%": 1,
      "Retention <50%": 0,
      "Retention not stated": 0,
    },
    singleSelect: true,
    bands: {
      95: "Retention ≥95%",
      85: "Retention 85–94%",
      70: "Retention 70–84%",
      50: "Retention 50–69%",
      0: "Retention <50%",
    },
  },
  population_diversity: {
    tier: 2,
    description:
      "Whether the participant pool represents multiple demographic groups or is drawn from a single WEIRD (Western, Educated, Industrialised, Rich, Democratic) population.",
    singleSelect: true,
    labels: [
      "Diverse population",
      "Homogeneous sample with justification",
      "WEIRD sample without justification",
    ],
    scoring: {
      "Diverse population": 5,
      "Homogeneous sample with justification": 2,
      "WEIRD sample without justification": 0,
    },
    labelDescriptions: {
      "Diverse population":
        "The text explicitly names participants from multiple racial or ethnic groups, or from countries spanning both WEIRD and non-WEIRD regions (Africa, Asia, Latin America, Middle East). Single-country studies (including large national cohorts such as UK Biobank or NHS cohorts), multi-site studies within one country, and multi-country studies confined to Western Europe or North America do NOT qualify — geographic scale within the WEIRD cluster is not diversity. The word 'diverse' in the text (e.g. 'diverse recruitment channels') does not indicate a diverse sample.",
      "Homogeneous sample with justification":
        "The sample is a defined population whose composition reflects the research question. Typical cases: observational cohorts or registries that enrol a specific patient group (disease-specific clinics, institutionalised patients, occupational exposure groups); national or regional health surveys where the country or region is the legitimate unit of study; large defined cohorts characterised by a biological trait (e.g. high milk tolerance). Country of study, institution type, and single-sex or single-age restriction are NOT disqualifiers — what matters is that the sample composition follows from the study's purpose.",
      "WEIRD sample without justification":
        "The sample is a convenience selection with no scientific rationale for its demographic composition. Typical cases: small or medium clinical trials and RCTs that recruited volunteer participants from Western clinical settings without the sample composition being the research focus (e.g. healthy adults, general overweight/obese adults, or condition-defined volunteers in a trial); large general-population tracking cohorts that enrol anyone who meets broad age or geographic criteria (UK Biobank, NHS cohorts, HPFS); non-human subjects (animal models, cell lines).",
    },
  },
  confounding: {
    tier: 2,
    description:
      "Whether the study identifies and statistically controls for variables that could independently explain the results.",
    labels: [
      "Confounders identified",
      "Confounders adjusted for",
      "Matching/propensity scoring used",
      "No confounding discussion",
    ],
    scoring: {
      "Confounders identified": 5,
      "Confounders adjusted for": 5,
      "Matching/propensity scoring used": 5,
      "No confounding discussion": 0,
    },
  },
  causation: {
    tier: 2,
    description:
      "Whether the study's design and language support, appropriately hedge, or overstate a causal relationship between exposure and outcome.",
    labels: [
      "Design supports causal inference",
      "Correlation only (no overclaiming)",
      "Overstates causation",
    ],
    scoring: {
      "Design supports causal inference": 10,
      "Correlation only (no overclaiming)": 5,
      "Overstates causation": 0,
    },
    singleSelect: true,
    labelDescriptions: {
      "Design supports causal inference":
        "The study uses a design that legitimately establishes causation (RCT, or a controlled animal/cell experiment with direct intervention), and findings are presented as causal conclusions.",
      "Correlation only (no overclaiming)":
        "Findings use appropriately hedged language ('associated with', 'correlated with', 'linked to', 'risk factor for') without claiming causation beyond what the design supports.",
      "Overstates causation":
        "Causal verbs ('causes', 'leads to', 'results in', 'induces', 'prevents') are applied to the study's own findings from a design that cannot support them — observational studies in humans, or translational claims to humans from pre-clinical data alone.",
    },
  },
  statistical_rigor: {
    tier: 2,
    description:
      "Whether the analysis corrects for multiple hypothesis tests and reports effect sizes with confidence intervals — two independent components of statistical reporting quality.",
    labels: [
      "Multiple comparisons addressed",
      "No adjustment for multiple comparisons",
      "Effect size & CIs reported",
      "Effect size or CIs reported (not both)",
    ],
    scoring: {
      "Multiple comparisons addressed": 5,
      "No adjustment for multiple comparisons": 0,
      "Effect size & CIs reported": 5,
      "Effect size or CIs reported (not both)": 2,
    },
    mutuallyExclusiveGroups: [
      ["Multiple comparisons addressed", "No adjustment for multiple comparisons"],
      ["Effect size & CIs reported", "Effect size or CIs reported (not both)"],
    ],
    labelDescriptions: {
      "Multiple comparisons addressed":
        "The text explicitly names a statistical correction method applied to account for conducting multiple hypothesis tests. Qualifying methods include: Bonferroni, Holm–Bonferroni, Benjamini–Hochberg, FDR or FWER control, Tukey's HSD, Dunnett's test, Scheffé, Newman–Keuls, or an explicit statement that p-values were corrected or adjusted for multiple comparisons or multiple testing. A post-hoc test named after a correction algorithm (e.g. 'Tukey post-hoc test') qualifies; a vague 'post-hoc analysis' without naming a correction algorithm does not.",
      "No adjustment for multiple comparisons":
        "The study ran inferential statistical tests (regression, ANOVA, t-test, chi-square, or reported p-values) across multiple outcomes, groups, or time points, but the text does not name any multiple-comparison correction method. This label is inferred from absence: if statistical tests are present but no correction method is named, apply this label. It does NOT require the authors to explicitly state 'no correction was made'.",
      "Effect size & CIs reported":
        "The text reports at least one named effect-size measure AND at least one confidence interval for a study outcome. Effect-size measures include: odds ratio (OR), hazard ratio (HR), risk ratio / relative risk (RR), risk difference, standardised mean difference (SMD), Cohen's d or f, eta-squared (η²), omega-squared, regression coefficient (β or B), correlation coefficient (Pearson r, Spearman ρ), or the phrase 'effect size'. Confidence intervals are reported as '95% CI', 'confidence interval', or equivalent. Both must be present for this label.",
      "Effect size or CIs reported (not both)":
        "The text reports either an effect-size measure OR confidence intervals, but not both. Examples: OR without a CI; a mean difference without a CI; a CI on a mean without naming an effect-size measure. Select this label when exactly one of the two elements is present.",
    },
  },
  statistical_power: {
    tier: 2,
    description:
      "Whether the study justified its sample size with a prospective power calculation performed before data collection.",
    labels: [
      "A priori power calculation reported",
      "Power calculation incomplete or underjustified",
      "No power calculation reported",
    ],
    scoring: {
      "A priori power calculation reported": 7,
      "Power calculation incomplete or underjustified": 3,
      "No power calculation reported": 0,
    },
    singleSelect: true,
    labelDescriptions: {
      "A priori power calculation reported":
        "The text explicitly reports a pre-study sample size or power calculation performed before data collection. Markers: an explicit power percentage ('80% power', 'power of 0.80'), a named power analysis tool (G*Power), the phrase 'a priori power calculation' or 'a priori sample size', 'powered to detect', 'adequately powered', or a sample-size calculation stating the number of participants needed to achieve a specified power. The calculation must be prospective — performed before the study began.",
      "Power calculation incomplete or underjustified":
        "A power or sample-size calculation is mentioned but is retrospective, post-hoc, or otherwise insufficient. Markers: 'post-hoc power', 'retrospective power', 'observed power', 'underpowered', 'insufficient power', 'limited power', or a sample-size calculation performed after data collection. Also applies when the paper mentions a calculation but provides no parameters or justification (e.g. 'sample size was determined by the available patient pool').",
      "No power calculation reported":
        "The text contains no mention of a power calculation or sample-size justification. This label is inferred from absence: the authors do not need to state that no calculation was done. If no power-related language appears in the text, apply this label. Note: reporting a result statistic like '95% power was achieved' in the results section does NOT constitute an a priori power calculation.",
    },
  },
  bias: {
    tier: 2,
    description:
      "Which systematic error sources the study's design or analysis explicitly addressed — each label is an independent positive indicator.",
    labels: [
      "Selection bias addressed",
      "Information bias addressed",
      "Time-based bias addressed",
      "Other biases addressed",
    ],
    labelDescriptions: {
      "Selection bias addressed":
        "The study uses design features that control who enters or remains in the study: " +
        "explicit randomisation procedure (computer-generated, block, stratified, sealed " +
        "envelopes), consecutive or unselected recruitment, matched controls, or a documented " +
        "eligibility/exclusion framework (CONSORT, PRISMA). Applies to any RCT or controlled " +
        "study that describes its allocation procedure — no explicit mention of 'selection bias' " +
        "is required.",
      "Information bias addressed":
        "The study uses design features that control measurement error or misclassification: " +
        "blinding of participants, investigators, lab analysts, or outcome assessors; use of " +
        "validated questionnaires or dietary assessment instruments; objective biomarker " +
        "measurement (plasma, blood, DXA, imaging) as the primary exposure or outcome " +
        "metric; or standardised data collection with trained assessors. No explicit mention " +
        "of 'information bias' is required.",
      "Time-based bias addressed":
        "The study uses design features that control period effects or temporal confounding: " +
        "crossover design with explicit washout periods between arms; period-balanced " +
        "sequence randomisation (Williams design, Latin square, randomised period order); " +
        "temporal sensitivity analyses in cohort studies (excluding the first 1–2 years of " +
        "follow-up to guard against reverse causality, midpoint-censoring analyses, lagged " +
        "exposure analyses); dense repeated measurements at standardised timepoints across " +
        "a structured temporal sequence; or Mendelian randomisation (genetic instruments " +
        "assigned at conception are inherently free of time-varying confounding).",
      "Other biases addressed":
        "The study uses structural or analytical features that address a specific bias not " +
        "covered by the three categories above: twin or sibling design (controls genetic and " +
        "familial confounding); competing risk analysis treating death or dropout as a " +
        "competing event (e.g. Fine-Gray models); pre-registration of the protocol " +
        "(ClinicalTrials.gov, IRCT, OSF — controls reporting and publication bias); active " +
        "dietary provision by the study rather than self-selection; unannounced dietary " +
        "recalls; propensity score matching or weighting; genetic knockout / mechanistic " +
        "specificity controls in experimental studies; isocaloric substitution analyses " +
        "explicitly replacing one dietary component calorie-for-calorie with another to " +
        "isolate its effect; or pre-specified sensitivity analyses with alternative model " +
        "specifications (alternative covariate sets, exclusion of specific subgroups, " +
        "alternative exposure or outcome definitions) that test the robustness of the main " +
        "finding against plausible sources of confounding.",
    },
    scoring: {
      "Selection bias addressed": 2,
      "Information bias addressed": 2,
      "Time-based bias addressed": 3,
      "Other biases addressed": 3,
    },
    labelSubsets: {
      // Time-based bias has no mechanism in preclinical studies (no washout/MR/period design).
      // rct and observational accept all four labels.
      preclinical: [
        "Selection bias addressed",
        "Information bias addressed",
        "Other biases addressed",
      ],
    },
  },
  funding: {
    tier: 2,
    description:
      "The independence of the study's funding source from the intervention being studied.",
    labels: [
      "Independent funding",
      "No conflicts of interest",
      "Industry funding without transparency",
    ],
    scoring: {
      "Independent funding": 7,
      "No conflicts of interest": 3,
      "Industry funding without transparency": 0,
    },
    mutuallyExclusiveGroups: [["Industry funding without transparency", "Independent funding"]],
  },
  peer_review: {
    tier: 2,
    description: "The editorial standing of the venue where the study was published.",
    labels: [
      "Top-tier journal",
      "Peer-reviewed reputable journal",
      "Preprint (not peer-reviewed)",
      "Predatory journal",
    ],
    scoring: {
      "Top-tier journal": 5,
      "Peer-reviewed reputable journal": 3,
      "Preprint (not peer-reviewed)": 0,
      "Predatory journal": 0,
    },
    singleSelect: true,
  },
};

// ── Derived vocab (backward-compat export) ────────────────────────────────────

/** Canonical label lists per dimension. Derived from SCORECARD. */
export const SCORECARD_VOCAB: Record<ScorecardKey, string[]> = Object.fromEntries(
  Object.entries(SCORECARD).map(([k, v]) => [k, v.labels])
) as Record<ScorecardKey, string[]>;

// ── Search & reranking config ─────────────────────────────────────────────────

/** Tunable parameters for the vector search and quality reranking steps. */
export const SEARCH_CONFIG = {
  /** Max documents kept from semantic (vector) search, best document score first, before quality filtering. */
  semanticLimit: 20,
  /**
   * Minimum cosine similarity for a paragraph to count toward its document. A document needs one
   * such paragraph to be a candidate, and its score is the sum of how far its best paragraphs
   * exceed this value.
   */
  semanticThreshold: 0.5,
  /**
   * Days a cached verification is served before it is recomputed regardless of invalidation. Also
   * bounds cache invalidation: a new document only invalidates claims verified within this window.
   * A new document invalidates a claim when one of its paragraphs reaches `semanticThreshold` for
   * the claim, so narrowing the search gate narrows invalidation with it.
   */
  cacheExpiryDays: 365,
  /** Max documents that count as evidence: the judge's sources once irrelevant ones are set aside. */
  qualityLimit: 5,
  /**
   * Extra documents the judge reads beyond qualityLimit, ranked after them. A reserve document
   * counts as evidence only in place of a higher-ranked one the judge found irrelevant, so the
   * verdict still rests on qualityLimit sources; it costs one more judge call per verification.
   */
  judgeReserve: 1,
  /**
   * Width of a relevance band, in document-score units. The documents passed to the judge are
   * ranked by band first (band 0 holds those within one width of the best document score, band
   * 1 the next width down, and so on) and by quality only within a band, so quality reorders
   * documents of comparable relevance but never lifts a distant match over a close one.
   */
  relevanceBandWidth: 0.05,
  /**
   * Maximum achievable quality score across all dimensions. Superseded by computeMaxScore()
   * for per-profile normalization (design-documents/025) — kept here only as a
   * document-independent reference; check callers before relying on it for reranking/scoring.
   */
  maxScore: 122,
  /**
   * Minimum normalized, design-weighted cross_design_quality [0, 1] to pass the re-ranking filter.
   * Deliberately kept at 0 — raising it above 0 could exclude Animal/Cell/Anecdotal-labeled
   * sources from LLM judging entirely (their cross_design_quality ceiling is well below 1.0
   * regardless of execution quality, see design-documents/025 step 4/5), not just rank them
   * lower. Raising this is a legitimate future decision, but must be made deliberately with
   * that finding in view, not as a side effect of an unrelated config tweak.
   */
  qualityThreshold: 0,
  /**
   * Execution quality [0, 1] assumed for the scorecard dimensions a source has not answered with
   * confidence, so a partly annotated source is neither penalised for the gap nor credited for
   * it. 0.5 is the midpoint of the scale: no evidence either way. Deliberately a constant, not a
   * corpus average — too few documents are fully annotated for an average to be reliable, and
   * the score of one paper should not move when others are annotated.
   */
  unansweredExecutionPrior: 0.5,
} as const;

/** `SEARCH_CONFIG.cacheExpiryDays` as a Postgres interval literal, for the cache RPCs. */
export function cacheExpiryInterval(): string {
  return `${SEARCH_CONFIG.cacheExpiryDays} days`;
}

// ── Auto-ingest decision config ───────────────────────────────────────────────

/** Tunable parameters for the corpus auto-ingest / escalate decision (evaluateIngestDecision). */
export const INGEST = {
  /**
   * Minimum extraction confidence every Tier-1 field must reach for a document to auto-ingest
   * without human review; any Tier-1 field below it escalates to a human instead.
   *
   * Tier 1 is deliberately `study_type` alone (design-documents/035): it is the one field whose
   * error changes a fact-check verdict rather than only a quality score, so it is the sole gate on
   * ingestion. Every other dimension is Tier 2 — it shapes the quality score and is still drafted
   * and reviewable, but never blocks a document from entering the corpus.
   *
   * Set at 0.925, just under the calibrated confidence of the study-type LLM (0.93, measured
   * on the final prompt: 93 papers in 100 right, with gross errors under 2 in 100). Human review
   * of the papers still in dispute no longer improves the label, so whatever the LLM returns for
   * `study_type` auto-ingests; only a document with no study type extracted escalates.
   */
  tier1ConfidenceFloor: 0.925,
} as const;

/**
 * Per-tier confidence thresholds above which a field is shown pre-approved in Kodakku rather
 * than prompted for review (TAN-34). Display-only — distinct from `INGEST.tier1ConfidenceFloor`,
 * which gates auto-ingestion. Tier 2 and Tier 3 never gate ingestion regardless of where
 * they sit relative to their threshold.
 */
export const AUTO_VALIDATE_THRESHOLD: Record<ExtractionTier, number> = {
  1: 0.925,
  2: 0.8,
  3: 0.8,
} as const;

/** Per-tier badge colour, mirroring Tangrowth's `_TIER_COLORS`
 *  (`tangrowth/tangrowth/services/benchmark_report.py`) so a tier reads as the same colour
 *  everywhere — the benchmark report and Kodakku's tier badges (FieldReviewPanel). */
export const TIER_COLOR: Record<ExtractionTier, string> = {
  1: "#dc2626",
  2: "#d97706",
  3: "#6b7280",
} as const;

// ── Verdict aggregation config ────────────────────────────────────────────────

/** Tunable parameters for the deterministic verdict aggregation step. */
export const AGGREGATION = {
  /** Half-life in years for recency decay (score halves every N years). */
  recency_half_life_years: 10,
  thresholds: {
    /** |signal| above which the verdict is SUPPORTED or CONTRADICTED. */
    strong: 0.6,
    /**
     * Share of the evidence weight that counts as significant: |signal| above it is a lean,
     * and a side holding more than it is a real position, so two such sides are MIXED.
     */
    lean: 0.2,
  },
  min_evidence: {
    /** Fewest sources that must test the claim for any verdict but NO_COVERAGE. */
    sources: 3,
  },
  /**
   * Top-K retrieved results paragraphs fed to the judge, plus one neighbour either side.
   * Only the results section is filtered — abstract and conclusion pass through whole.
   * Tune this to keep p95 judge payload ≤ 6,000 chars total.
   */
  judge_results_top_k: 3,
} as const;

/**
 * Retrieval config for expert insights.
 * min_similarity tuned to 0.75 per INS-09 gate: at this floor the false-attach gap
 * (highest off-topic similarity 0.726) sits just below the true-attach floor (0.755),
 * giving clean separation with the current 10-insight corpus.
 */
export const INSIGHT_RETRIEVAL = {
  /** Maximum number of insights to attach per fact-check. */
  max_results: 3,
  /** Minimum cosine similarity for an insight to be attached. Below this = silence. */
  min_similarity: 0.75,
} as const;
